import { useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config';

/**
 * Format a backend error payload into a single human-readable string.
 * Handles:
 *  - { message } / { error }       → message
 *  - { errors: { json: { field: [msg, ...], ... } } }  (marshmallow/webargs)
 *  - { errors: { field: [msg, ...] } }                 (flat marshmallow)
 *  - falls back to the provided default.
 */
function formatApiError(json, fallback) {
  if (!json || typeof json !== 'object') return fallback;
  if (typeof json.message === 'string' && json.message) return json.message;
  if (typeof json.error === 'string' && json.error) return json.error;

  const fieldErrors = json.errors?.json && typeof json.errors.json === 'object'
    ? json.errors.json
    : (json.errors && typeof json.errors === 'object' ? json.errors : null);

  if (fieldErrors) {
    const parts = Object.entries(fieldErrors).map(([field, msgs]) => {
      const text = Array.isArray(msgs) ? msgs.join(', ') : String(msgs);
      return `${field}: ${text}`;
    });
    if (parts.length) return parts.join(' · ');
  }
  return fallback;
}

/**
 * API hook — module-level singleton pattern.
 * Handles auth token injection, 401 auto-logout, 204 responses.
 */
export function useApi() {
  const { token, logout } = useAuth();

  const request = useCallback(async (path, options = {}) => {
    const { method = 'GET', body, headers: custom, ...rest } = options;

    const res = await fetch(API_BASE_URL + path, {
      method,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'auth-token': token || '',
        ...custom,
      },
      body,
      ...rest,
    });

    if (res.status === 401) {
      logout();
      throw new Error('Unauthorized');
    }
    if (res.status === 204) return null;
    if (!res.ok) {
      const fallback = `Error ${res.status}`;
      let msg = fallback;
      try {
        const json = await res.json();
        msg = formatApiError(json, fallback);
      } catch { /* ignore */ }
      throw new Error(msg);
    }
    return res.json();
  }, [token, logout]);

  const postForm = useCallback(async (path, formData) => {
    const res = await fetch(API_BASE_URL + path, {
      method: 'POST',
      headers: {
        // Do NOT set Content-Type — the browser sets the multipart boundary.
        'Accept': 'application/json',
        'auth-token': token || '',
      },
      body: formData,
    });
    if (res.status === 401) {
      logout();
      throw new Error('Unauthorized');
    }
    if (res.status === 204) return null;
    if (!res.ok) {
      const fallback = `Error ${res.status}`;
      let msg = fallback;
      try {
        const json = await res.json();
        msg = formatApiError(json, fallback);
      } catch { /* ignore */ }
      throw new Error(msg);
    }
    return res.json();
  }, [token, logout]);

  return useMemo(() => ({
    get: (path) => request(path),
    post: (path, body) => request(path, { method: 'POST', body: JSON.stringify(body) }),
    put: (path, body) => request(path, { method: 'PUT', body: JSON.stringify(body) }),
    del: (path) => request(path, { method: 'DELETE' }),
    postForm: (path, formData) => postForm(path, formData),
  }), [request, postForm]);
}

